import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import type { CopyNurses } from "../Roster_create";
import type { FullNurseSchemaAddPreceptor } from "../Roster_create";
import { computeCopyNurses } from "@utils/rosterCreate-util.ts";
import type { NurseResponse } from "@/types/nurse-type";

interface UseRosterEditingParams {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rosterData: any;
  fullNurseSchema: NurseResponse[];
  fullNurseSchemaAddPreceptor: FullNurseSchemaAddPreceptor | undefined;
  curDate: { year: number | null; month: number | null } | null;
  rosterContextKey: string;
}

function preserveEditedSchedules(
  freshRows: CopyNurses,
  currentRows: CopyNurses
): CopyNurses {
  const currentByNurseId = new Map(
    currentRows.map((row) => [String(row.nurse_id), row])
  );

  return freshRows.map((freshRow) => {
    const currentRow = currentByNurseId.get(String(freshRow.nurse_id));
    if (!currentRow) return freshRow;
    return {
      ...freshRow,
      schedule: currentRow.schedule,
      schedule_ids: currentRow.schedule_ids,
    };
  });
}

export default function useRosterEditing({
  rosterData,
  fullNurseSchema,
  fullNurseSchemaAddPreceptor,
  curDate,
  rosterContextKey,
}: UseRosterEditingParams) {
  const [copyNurses, setCopyNurses] = useState<CopyNurses>([]);
  const [editingSource, setEditingSource] = useState<{
    readonly data: unknown;
    readonly contextKey: string;
  } | null>(null);
  const previousRosterDataRef = useRef(rosterData);
  const previousDateKeyRef = useRef(
    `${curDate?.year ?? ""}-${curDate?.month ?? ""}`
  );
  const previousRosterContextKeyRef = useRef(rosterContextKey);

  const [modifiedCells, setModifiedCells] = useState<Set<string>>(new Set());

  // 수정된 셀 추가 함수
  const addModifiedCell = useCallback((nurseId: string, dayIndex: number) => {
    setModifiedCells((prev) => {
      const next = new Set(prev);
      next.add(`${nurseId}-${dayIndex}`);
      return next;
    });
  }, []);

  // 수정된 셀 전체 초기화 (편집 모드 종료, Undo 시 사용)
  const clearModifiedCells = useCallback(() => {
    setModifiedCells(new Set());
  }, []);
  // rosterData가 변경되면 modifiedCells 초기화 (저장 후 데이터 갱신 시)
  useEffect(() => {
    setModifiedCells(new Set());
  }, [rosterData]);

  // copyNurses 초기값을 useMemo로 계산 (유틸 함수 사용)
  const initialCopyNurses = useMemo(() => {
    return computeCopyNurses({
      scheduleData: rosterData ?? null,
      fullNurseSchema,
      fullNurseSchemaAddPreceptor,
      curDate,
    });
  }, [rosterData, fullNurseSchema, fullNurseSchemaAddPreceptor, curDate]);

  // copyNurses 초기값 동기화 (스케줄 데이터나 간호사 스키마 변경 시)
  useEffect(() => {
    if (fullNurseSchema.length > 0 && initialCopyNurses.length > 0) {
      const dateKey = `${curDate?.year ?? ""}-${curDate?.month ?? ""}`;
      const scheduleSourceChanged =
        previousRosterDataRef.current !== rosterData ||
        previousDateKeyRef.current !== dateKey ||
        previousRosterContextKeyRef.current !== rosterContextKey;

      setCopyNurses((currentRows) =>
        scheduleSourceChanged || currentRows.length === 0
          ? initialCopyNurses
          : preserveEditedSchedules(initialCopyNurses, currentRows)
      );
      setEditingSource({ data: rosterData, contextKey: rosterContextKey });
      previousRosterDataRef.current = rosterData;
      previousDateKeyRef.current = dateKey;
      previousRosterContextKeyRef.current = rosterContextKey;
    }
  }, [
    curDate?.month,
    curDate?.year,
    fullNurseSchema.length,
    initialCopyNurses,
    rosterContextKey,
    rosterData,
  ]);

  // 근무표가 비어있는지 확인
  const isRosterEmpty = useMemo(() => {
    if (!copyNurses || copyNurses.length === 0) return true;
    return copyNurses.every(
      (nurse) =>
        !nurse.schedule ||
        nurse.schedule.every(
          (shift) => shift === "-" || shift === "주" || !shift
        )
    );
  }, [copyNurses]);

  return {
    isEditingReady:
      rosterData != null &&
      editingSource?.data === rosterData &&
      editingSource?.contextKey === rosterContextKey,
    copyNurses,
    setCopyNurses,
    isRosterEmpty,
    modifiedCells,
    setModifiedCells,
    addModifiedCell,
    clearModifiedCells,
  };
}
